import { useState } from 'react';
import { isPendingItem, type DailyChecklistItem } from '../../lib/dailyChecklist';
import { CHECKBOX_BOX, Input } from '../ui';
import { CheckIcon, TrashIcon } from '../icons';

/**
 * A day's rows.
 *
 * Shared by the dashboard card and by an expanded day on the history page, which
 * is what `canEdit` is for: a past day is a record, so it keeps its tick boxes
 * and loses the rename and the delete. That rule lives here and in the card's
 * composer, and deliberately not on the server -- the note at the top of
 * `routes/daily-checklist.ts` says why.
 */
export function DailyChecklistList({
  items,
  canEdit,
  onToggle,
  onRename,
  onRemove,
}: {
  items: DailyChecklistItem[];
  canEdit: boolean;
  onToggle: (item: DailyChecklistItem) => void;
  onRename: (item: DailyChecklistItem, draft: string) => void;
  onRemove: (item: DailyChecklistItem) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <ul className="divide-y divide-stone-100">
      {items.map((item) => (
        <li key={item.id} className="group/item flex items-start gap-2.5 px-4 py-2">
          <button
            type="button"
            // `role="checkbox"` on a button rather than a real input, the same
            // choice TaskRow, FocusTaskRow and TaskChecklist make: the forms
            // plugin is opt-in here, so a native control renders as unstyled
            // platform chrome.
            role="checkbox"
            aria-checked={item.done}
            // No id to PATCH yet.
            disabled={isPendingItem(item.id)}
            onClick={() => onToggle(item)}
            aria-label={`${item.done ? 'Untick' : 'Tick'}: ${item.title}`}
            // `mt-0.5` because these lines wrap: the box belongs beside the
            // first line of the text, not centred against three of them.
            className={`mt-0.5 shrink-0 ${CHECKBOX_BOX} disabled:cursor-default disabled:opacity-50 ${
              item.done
                ? 'border-emerald-500 bg-emerald-500 text-white'
                : 'border-stone-300 text-transparent hover:border-emerald-500 hover:text-emerald-500'
            }`}
          >
            <CheckIcon className="h-2.5 w-2.5" />
          </button>

          {editingId === item.id ? (
            <DailyItemInput
              title={item.title}
              onCommit={(draft) => {
                setEditingId(null);
                onRename(item, draft);
              }}
              onCancel={() => setEditingId(null)}
            />
          ) : canEdit ? (
            <button
              type="button"
              title="Click to rename"
              disabled={isPendingItem(item.id)}
              onClick={() => setEditingId(item.id)}
              className={`-mx-1 min-w-0 flex-1 break-words rounded px-1 py-0.5 text-left text-dense transition hover:bg-stone-100 disabled:hover:bg-transparent ${
                item.done ? 'text-stone-400 line-through' : 'text-stone-800'
              }`}
            >
              {item.title}
            </button>
          ) : (
            // A past day reads rather than edits, so the title is text. Same
            // padding as the button above, so the two views line up exactly.
            <span
              className={`min-w-0 flex-1 break-words px-0 py-0.5 text-dense ${
                item.done ? 'text-stone-400 line-through' : 'text-stone-800'
              }`}
            >
              {item.title}
            </span>
          )}

          {isPendingItem(item.id) ? (
            <span className="mt-0.5 shrink-0 text-meta text-stone-500">Saving…</span>
          ) : canEdit ? (
            <button
              type="button"
              aria-label={`Remove ${item.title}`}
              onClick={() => onRemove(item)}
              // `opacity-0` rather than `invisible`, as in TaskChecklist: there
              // is no full-bleed row link here for it to hit-test above.
              className="mt-0.5 shrink-0 rounded p-0.5 text-stone-500 opacity-0 transition hover:bg-red-50 hover:text-red-700 focus-visible:opacity-100 group-hover/item:opacity-100"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * The rename field.
 *
 * Split out so it holds its own draft: keeping that in the list would re-render
 * every sibling row on each keystroke. Escape calls `preventDefault` for the
 * reason TaskChecklist's copy documents -- nothing on the dashboard listens for
 * it today, but this component is one move away from a panel that does.
 */
function DailyItemInput({
  title,
  onCommit,
  onCancel,
}: {
  title: string;
  onCommit: (draft: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(title);

  return (
    <Input
      compact
      autoFocus
      value={draft}
      aria-label="Checklist item"
      className="min-w-0 flex-1"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(draft);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}
